/**
 * Logique pure du formulaire d'avis post-live : enchaînement des étapes selon la note, condition
 * d'activation du bouton d'envoi et construction du payload envoyé à l'API. L'éligibilité (durée,
 * pic de participants, rôle) est décidée côté back, le front ne fait que la consommer.
 */

export const MIN_RATING = 1;
export const MAX_RATING = 5;

/** Au-dessus du seuil on demande des suggestions, en dessous on demande ce qui peut être amélioré. */
export const POSITIVE_RATING_THRESHOLD = 4;

export const MAX_COMMENT_LENGTH = 2000;

export type PostLiveFeedbackStep = 'rating' | 'suggestion' | 'improvement';

/** Réponse à « avez-vous des suggestions ? », proposée uniquement pour une note positive. */
export type PostLiveFeedbackSuggestionChoice = 'YES' | 'NOTHING_TO_REPORT';

export interface PostLiveFeedbackFormState {
  rating: number | null;
  suggestionChoice: PostLiveFeedbackSuggestionChoice | null;
  comment: string;
}

export const emptyPostLiveFeedbackForm: PostLiveFeedbackFormState = {
  rating: null,
  suggestionChoice: null,
  comment: '',
};

export const isPositiveRating = (rating: number): boolean => rating >= POSITIVE_RATING_THRESHOLD;

export const getPostLiveFeedbackStep = (rating: number | null): PostLiveFeedbackStep => {
  if (rating === null) return 'rating';
  return isPositiveRating(rating) ? 'suggestion' : 'improvement';
};

/** Le champ libre n'apparaît qu'après un choix explicite sur une note positive. */
export const isCommentVisible = (state: PostLiveFeedbackFormState): boolean => {
  const step = getPostLiveFeedbackStep(state.rating);
  if (step === 'improvement') return true;
  return step === 'suggestion' && state.suggestionChoice === 'YES';
};

export const canSubmitPostLiveFeedback = (state: PostLiveFeedbackFormState): boolean => {
  const { rating, suggestionChoice, comment } = state;
  if (rating === null || rating < MIN_RATING || rating > MAX_RATING) return false;
  if (comment.length > MAX_COMMENT_LENGTH) return false;

  if (getPostLiveFeedbackStep(rating) === 'improvement') {
    return comment.trim() !== '';
  }
  if (suggestionChoice === 'NOTHING_TO_REPORT') return true;
  return suggestionChoice === 'YES' && comment.trim() !== '';
};

export interface PostLiveFeedbackPayload {
  rating: number;
  comment?: string;
}

export const buildPostLiveFeedbackPayload = (
  state: PostLiveFeedbackFormState,
): PostLiveFeedbackPayload | null => {
  if (!canSubmitPostLiveFeedback(state)) return null;

  const trimmedComment = isCommentVisible(state) ? state.comment.trim() : '';
  return {
    rating: state.rating as number,
    ...(trimmedComment === '' ? {} : { comment: trimmedComment }),
  };
};

export type PostLiveFeedbackConfirmation = 'POSITIVE' | 'FOLLOW_UP';

export const getPostLiveFeedbackConfirmation = (rating: number): PostLiveFeedbackConfirmation =>
  isPositiveRating(rating) ? 'POSITIVE' : 'FOLLOW_UP';
